"use client"

import { motion } from "framer-motion"
import { Card, CardContent } from "./ui/card"
import { Button } from "./ui/button"
import { ArrowRight } from "lucide-react"

const journalPosts = [
  {
    slug: "tables-chairs-conversations",
    title: "Tables, Chairs & Conversations: Designing the Heart of the Home",
    excerpt: "Discover how thoughtful design choices can transform your daily rituals into moments of mindfulness.",
    category: "Furniture Guide",
    date: "June 12, 2023",
    image: "/Blogs_1.png?height=400&width=600",
  },
  {
    slug: "lighting-the-way",
    title: "Lighting the Way: When Lamps Become Works of Art",
    excerpt: "Discover how artistic lighting pieces can transform any space into a gallery of warmth and personality.",
    category: "Design Fusion",
    date: "May 28, 2023",
    image: "/Blogs_2.png?height=400&width=600",
  },
  {
    slug: "craft-of-comfort",
    title: "The Craft of Comfort: Behind the Scenes with Our Artisanal Partners",
    excerpt: "Explore the craftsmanship, heritage, and modern design behind Mi Casa Su Casa's artisanal furniture, sculptures, and décor.",
    category: "Craftsmanship",
    date: "May 15, 2023",
    image: "/Blogs_3.png?height=400&width=600",
  },
  {
    slug: "layered-living",
    title: "Layered Living: Styling Your Space with Furniture, Lighting & Textiles",
    excerpt: "Learn how to style your home with layered furniture, lighting & textiles. Create a magazine-worthy look with Mi Casa Su Casa's collection.",
    category: "Styling Guide",
    date: "May 1, 2023",
    image: "/Blogs_4.png?height=400&width=600",
  },
]

export default function StyleJournal() {
  return (
    <section className="bg-stone-50 py-24">
      <div className="container mx-auto px-4">
        <motion.h2
          className="mb-3 text-center font-serif text-3xl font-light md:text-4xl"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          Inspiration You Can Live In
        </motion.h2>

        <motion.p
          className="mx-auto mb-12 max-w-2xl text-center text-muted-foreground"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          Explore our journal for design insights, home styling tips, and artisanal stories.
        </motion.p>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {journalPosts.map((post, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className="h-full"
            >
              <Card className="h-full border-none shadow-md">
                <div className="relative h-48 w-full overflow-hidden">
                  <img
                    src={post.image || "/placeholder.svg"}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                </div>

                <CardContent className="p-5">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium uppercase tracking-wider text-amber-700">{post.category}</span>
                    <span className="text-xs text-muted-foreground">{post.date}</span>
                  </div>

                  <h3 className="mb-2 font-serif text-xl font-medium">{post.title}</h3>
                  <p className="mb-3 text-muted-foreground">{post.excerpt}</p>

                  <a href={`/blog/${post.slug}`} className="group p-0 text-amber-700 inline-flex items-center font-medium">
                    Read More <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </a>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <a href="/blog" className="inline-flex items-center justify-center px-8 py-2 border border-stone-300 text-stone-800 font-medium transition-colors hover:bg-stone-50 focus:outline-none focus:ring-2 focus:ring-stone-300 focus:ring-offset-2">
            Read the Journal
          </a>
        </div>
      </div>
    </section>
  )
} 